/**
 * Copyright (C) Firstlight Media. All rights reserved.
 */

import { Deferred, Logger } from '@quickplay/foundation';
import { AdsRenderingSettings } from '../../../models/AdsRenderingSettings';
import { IMAAdRequest } from '../../../models/csai/IMAAdRequest';
import { createAdsRequest, isRequestContextMatch, setAdsRenderingSettings, setImaSdkSettings } from '../../util/AdUtil';
import { ImaSdkSettings } from '../../../models/ImaSdkSettings';

export interface ContentAdsManagerResolverConfig {
  adsLoader: google.ima.AdsLoader;
  adRequest: IMAAdRequest;
  adsRenderingSettings?: AdsRenderingSettings;
  imaSDK: typeof google.ima;
  videoElement: HTMLVideoElement;
  requestContext: string;
  logger: Logger;
  imaSdkSettings?: ImaSdkSettings;
}

/**
 * Creates an IMA ad request and resolves with the corresponding AdsManager.
 * Starts fetching on construction, so the AdsManager is usually ready by the time it is needed.
 */
export class ContentAdsManagerResolver {
  private readonly adsLoader: google.ima.AdsLoader;
  private readonly adRequest: IMAAdRequest;
  private readonly adsRenderingSettings?: AdsRenderingSettings;
  private readonly imaSDK: typeof google.ima;
  private readonly videoElement: HTMLVideoElement;
  private readonly requestContext: string;
  private readonly logger: Logger;
  private readonly imaSdkSettings?: ImaSdkSettings;

  /** The loaded AdsManager, or null if none is cached. */
  private adsManager: google.ima.AdsManager | null = null;
  /** The in-flight ad request, or null if nothing is being fetched. */
  private pending: Deferred<any> | null = null;

  constructor(config: ContentAdsManagerResolverConfig) {
    this.adsLoader = config.adsLoader;
    this.adRequest = config.adRequest;
    this.adsRenderingSettings = config.adsRenderingSettings;
    this.imaSDK = config.imaSDK;
    this.videoElement = config.videoElement;
    this.requestContext = config.requestContext;
    this.logger = config.logger;
    this.imaSdkSettings = config.imaSdkSettings;

    this.startBackgroundFetch();
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Returns the AdsManager.
   * - If one is cached, it resolves immediately.
   * - If a request is in flight, it shares that request.
   * - Otherwise it starts a new request (this is also the retry path after a failure).
   */
  public fetchAdsManager(): Promise<google.ima.AdsManager | null> {
    if (this.adsManager) {
      return Promise.resolve(this.adsManager);
    }

    return this.pending?.promise ?? this.requestNewAdsManager();
  }

  // ---------------------------------------------------------------------------
  // Request lifecycle (private)
  // ---------------------------------------------------------------------------

  /**
   * Starts a fetch without anyone awaiting it (used by the constructor).
   * The rejection is caught here to avoid an unhandled promise rejection.
   */
  private startBackgroundFetch(): void {
    this.fetchAdsManager().catch((error: unknown) => {
      this.logger.warn('[ContentAdsManagerResolver] Background fetch failed:', this.requestContext, error);
    });
  }

  /**
   * Sends a new ad request through the shared AdsLoader.
   * Registers the loader listeners, creates the pending request and returns its promise.
   * It is settled later by onAdsManagerLoaded (success) or onAdsLoaderError (failure).
   */
  private requestNewAdsManager(): Promise<google.ima.AdsManager | null> {
    this.logger.info('[ContentAdsManagerResolver] Requesting AdsManager', this.requestContext);

    const deferred = new Deferred<google.ima.AdsManager | null>();
    const userRequestContext = { requestContext: this.requestContext };

    this.pending = deferred;
    this.toggleLoaderListeners(true);

    try {
      if (this.imaSdkSettings && this.imaSDK?.settings) {
        setImaSdkSettings(this.imaSDK.settings, this.imaSdkSettings);
      }

      const imaRequest = createAdsRequest(this.imaSDK, this.adRequest);

      this.adsLoader.requestAds(imaRequest, userRequestContext);
    } catch (error: unknown) {
      this.markRequestFailed(error);
    }

    return deferred.promise;
  }

  /** Caches the loaded AdsManager and resolves whoever is waiting on fetchAdsManager(). */
  private markAdsManagerReady(adsManager: google.ima.AdsManager): void {
    this.logger.info('[ContentAdsManagerResolver] AdsManager resolved', this.requestContext);

    this.toggleLoaderListeners(false);
    this.adsManager = adsManager;

    this.detachPendingRequest()?.resolve(adsManager);
  }

  /**
   * Rejects whoever is waiting on fetchAdsManager().
   * The pending slot is cleared, so the next fetchAdsManager() retries.
   */
  private markRequestFailed(error: unknown): void {
    this.logger.debug('[ContentAdsManagerResolver] AdsManager request failed', this.requestContext, error);

    this.toggleLoaderListeners(false);

    this.detachPendingRequest()?.reject(error);
  }

  /** Removes the in-flight request from this fetcher and returns it, so it is settled exactly once. */
  private detachPendingRequest(): Deferred<any> | null {
    const request = this.pending;

    this.pending = null;
    return request;
  }

  // ---------------------------------------------------------------------------
  // IMA event handlers
  // ---------------------------------------------------------------------------

  /**
   * AdsLoader fired ADS_MANAGER_LOADED.
   * Ignores events from other requests, builds the rendering settings and creates
   * the AdsManager on our video element.
   */
  private readonly onAdsManagerLoaded = (event: google.ima.AdsManagerLoadedEvent): void => {
    if (!this.isEventForThisRequest(event)) {
      return;
    }

    try {
      const renderingSettings = setAdsRenderingSettings(
        new this.imaSDK.AdsRenderingSettings(),
        this.adsRenderingSettings,
        this.imaSDK,
      );

      renderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;

      this.markAdsManagerReady(event.getAdsManager(this.videoElement, renderingSettings));
    } catch (error) {
      this.markRequestFailed(error);
    }
  };

  /** AdsLoader fired AD_ERROR. Ignores errors from other requests. */
  private readonly onAdsLoaderError = (event: google.ima.AdErrorEvent): void => {
    if (!this.isEventForThisRequest(event)) {
      return;
    }

    this.markRequestFailed(event?.getError?.() ?? event);
  };

  /**
   * True if the IMA event belongs to this fetcher's ad request.
   * The AdsLoader is shared, so events from other requests also arrive here.
   */
  private isEventForThisRequest(event: google.ima.AdErrorEvent | google.ima.AdsManagerLoadedEvent): boolean {
    return isRequestContextMatch(event?.getUserRequestContext?.(), this.requestContext);
  }

  // ---------------------------------------------------------------------------
  // Listener wiring
  // ---------------------------------------------------------------------------

  /** Adds (true) or removes (false) the ADS_MANAGER_LOADED and AD_ERROR listeners on the AdsLoader. */
  private toggleLoaderListeners(attach: boolean): void {
    const { AdsManagerLoadedEvent, AdErrorEvent } = this.imaSDK;
    const method = attach ? 'addEventListener' : 'removeEventListener';

    this.adsLoader[method](AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, this.onAdsManagerLoaded, false);
    this.adsLoader[method](AdErrorEvent.Type.AD_ERROR, this.onAdsLoaderError, false);
  }
}
